import { useQuery } from '@tanstack/react-query';

import { getEvent, getEventLots, getLotPage } from '@features/buyer-line-items-pagination/api/buyer-line-items-pagination-client';
import { paginationKeys } from '@features/buyer-line-items-pagination/api/keys';
import type { LotPageRequest, LotPageResult } from '@features/buyer-line-items-pagination/types/lot-page';
import type { QueryClient } from '@tanstack/react-query';
import type { ID } from 'types';

// The provider's load step reads the event and the lot list, not a mounted component, so both go
// through `fetchQuery`. No `staleTime`, so every load and refresh requests them again.
export const fetchEvent = (queryClient: QueryClient, eventId: ID) => queryClient.fetchQuery({
  queryFn: () => getEvent(eventId),
  queryKey: paginationKeys.event(eventId),
});

export const fetchEventLots = (queryClient: QueryClient, eventId: ID) => queryClient.fetchQuery({
  queryFn: () => getEventLots(eventId),
  queryKey: paginationKeys.eventLots(eventId),
});

interface UseLotPageQueryParams extends LotPageRequest {
  enabled: boolean;
  getRefreshMarker: () => number;
}

/** A page is stale at once, so a key change, a revisit and an invalidation all read it again. */
export const useLotPageQuery = ({ enabled, eventId, getRefreshMarker, lotId, page, pageSize, search }: UseLotPageQueryParams) =>
  useQuery({
    enabled,
    queryFn: async (): Promise<LotPageResult> => {
      const refreshMarker = getRefreshMarker();
      const lotPage = await getLotPage({ eventId, lotId, page, pageSize, search });

      return { ...lotPage, refreshMarker };
    },
    queryKey: paginationKeys.lotPage(eventId, lotId, page, pageSize, search),
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
  });
